/**
 * Generic pagination types used across the UI.
 * Keep this small and focused so hooks and CollectionShell share a common contract.
 */

export type PageRequest = {
  limit?: number;
  /** Cursor for forward pagination (opaque string returned by the server) */
  next?: string | null;
  /** Arbitrary filter payload passed to services (query params/body).
   * Can be a raw string or a structured record. */
  filter?: string | Record<string, unknown>;
};

export type Page<T> = {
  items: T[];
  /** Cursor for next page, if available */
  next?: string | null;
  /** Cursor for previous page, if available */
  prev?: string | null;
  /** Optional total count when the API provides it */
  total?: number;
  /** Echo of the page size used to fetch this page */
  limit?: number;
};

/** Alias for clarity in places that call out adapter envelopes vs. UI pages */
export type PageEnvelope<T> = Page<T>;

/**
 * Flatten an array of pages returned by react-query's useInfiniteQuery into a single items array.
 */
export function flattenPages<T>(pages?: Array<Page<T>>): T[] {
  if (!pages || pages.length === 0) return [];
  return pages.reduce<T[]>((acc, p) => {
    if (p?.items?.length) acc.push(...p.items);
    return acc;
  }, []);
}

/**
 * Map an adapter-style page payload (e.g. { Models?: T[]; Next?: string; Prev?: string })
 * into the UI-friendly `Page<T>` shape.
 */
export function mapAdapterPage<
  T,
  A extends { Models?: T[]; Next?: string | null; Prev?: string | null },
>(adapter: A, extras?: { total?: number; limit?: number }): Page<T> {
  return {
    items: adapter.Models ?? [],
    next: adapter.Next ?? null,
    prev: adapter.Prev ?? null,
    total: extras?.total,
    limit: extras?.limit,
  };
}

/**
 * Convert our UI PageRequest into the adapter query shape.
 * This centralizes null/serialization handling so services remain small.
 */
export function toApiQuery(req?: PageRequest): {
  limit?: number;
  next?: string;
  filter?: string;
} {
  const out: { limit?: number; next?: string; filter?: string } = {};
  if (!req) return out;
  if (typeof req.limit === 'number' && req.limit > 0) out.limit = req.limit;
  if (req.next) out.next = req.next; // skip null/empty values
  if (req.filter !== undefined) {
    out.filter =
      typeof req.filter === 'string' ? req.filter : JSON.stringify(req.filter);
  }
  return out;
}
